import { activityChanges, activityValues, itemName, formatValue } from './activityChanges';
import { describeEngagement } from './engagementEvents';

// Turns a log entry into what the activity entry shows (US 9438): who did what to which item, and what changed.
// One table of item types and one of relations replace the renderer per logged type.

// Kinds of items: label (singular noun), icon (as in the side menu) and the page that shows one
const KINDS = {
  process: { label: 'processes.log.title', icon: 'cube', route: 'processes.view' },
  activity: { label: 'activities.log.title', icon: 'cube', route: 'activities.view' },
  system: { label: 'systems.log.title', icon: 'desktop', route: 'systems.view' },
  contract: { label: 'maintenance.third_parties.log.title', icon: 'exchange-alt', route: 'contracts.view' },
  vendor: { label: 'maintenance.third_parties.vendors.log.title', icon: 'building', route: 'contracts.third_parties.view' },
  risk: { label: 'risk_scenarios.log.title', icon: 'umbrella', route: 'risk_scenarios.view' },
  policy: { label: 'maintenance.policies.log.title', icon: 'file-invoice', route: 'policies.view' },
  measure: { label: 'maintenance.measures.log.title', icon: 'toolbox', route: 'maintenance.measures.view' },
  control: { label: 'generic_controls.log.title', icon: 'shield-alt', route: 'generic_controls.view' },
  framework: { label: 'maintenance.frameworks.log.title', icon: 'book', route: 'maintenance.frameworks.view' },
  template: { label: 'maintenance.assessments.log.title', icon: 'clipboard-list', route: 'maintenance.assessments.view' },
  question: { label: 'maintenance.questions.log.title', icon: 'tasks' },
  objective: { label: 'template_view.history.objective', icon: 'list-ul' },
  project: { label: 'project.log.title', icon: 'calendar-alt', route: 'projects.view', name: 'title' },
  task: { label: 'tasks.log.title', icon: 'check-square', name: 'title' },
  incident_category: { label: 'maintenance.incidents.incident_categories.log.title', icon: 'exclamation-triangle', route: 'maintenance.incident_categories.view' },
  user: { label: 'users_page.log.user', icon: 'user' },
  data_object: { label: 'maintenance.data_objects.log.title', icon: 'database', route: 'maintenance.data_objects.view' },
  process_group: { label: 'maintenance.process_groups.log.title', icon: 'layer-group', list: 'maintenance.process_groups' },
  organization: { label: 'maintenance.organizations.log.title', icon: 'building', route: 'maintenance.organizations.view' },
  article: { label: 'maintenance.legal_basis.log.title', icon: 'balance-scale', route: 'maintenance.legal_basis.view' },
  data_subject: { label: 'maintenance.data_subjects.log.title', icon: 'users', route: 'maintenance.data_subjects.view' },
  information_obligation: { label: 'maintenance.information_obligation.log.title', icon: 'info-circle', route: 'maintenance.information_obligation.view' },
  retention_rule: { label: 'maintenance.retention_rules.log.title', icon: 'hourglass-half', route: 'maintenance.retention_rules.view' },
  risk_category: { label: 'maintenance.risk_categories.log.title', icon: 'tags', route: 'maintenance.risk_categories.view' },
  threat: { label: 'maintenance.threats.log.title', icon: 'crosshairs', route: 'maintenance.threats.view' },
  vulnerability: { label: 'maintenance.vulnerabilities.log.title', icon: 'virus', route: 'maintenance.vulnerabilities.view' },
  partition: { label: 'maintenance.partitions.log.title', icon: 'layer-group', route: 'maintenance.partitionMembers' }
};

// Logged items of their own
const ITEMS = {
  'App\\Process\\Models\\Process': 'process',
  'App\\Activity\\Models\\Activity': 'activity',
  'App\\System\\Models\\System': 'system',
  'App\\Maintenance\\ThirdParty\\Models\\ThirdParty': 'contract',
  'App\\Maintenance\\ThirdParty\\Models\\Vendor': 'vendor',
  'App\\RiskScenario\\Models\\RiskScenario': 'risk',
  'App\\Maintenance\\Policy\\Models\\Policy': 'policy',
  'App\\Maintenance\\Measure\\Models\\Measure': 'measure',
  'App\\GenericControl\\Models\\GenericControl': 'control',
  'App\\Maintenance\\Framework\\Models\\Framework': 'framework',
  'App\\Maintenance\\Assessment\\Models\\Assessment': 'template',
  'App\\Project\\Models\\Project': 'project',
  'App\\Incident\\Models\\IncidentCategory': 'incident_category',
  'App\\Maintenance\\DataObject\\Models\\DataObject': 'data_object',
  'App\\Maintenance\\ProcessGroup\\Models\\ProcessGroup': 'process_group',
  'App\\Maintenance\\Organization\\Models\\Organization': 'organization',
  'App\\Maintenance\\Article\\Models\\Article': 'article',
  'App\\Maintenance\\DataSubject\\Models\\DataSubject': 'data_subject',
  'App\\Maintenance\\InformationObligation\\Models\\InformationObligation': 'information_obligation',
  'App\\Maintenance\\RetentionRule\\Models\\RetentionRule': 'retention_rule',
  'App\\Maintenance\\RiskCategory\\Models\\RiskCategory': 'risk_category',
  'App\\Maintenance\\Threat\\Models\\Threat': 'threat',
  'App\\Maintenance\\Vulnerability\\Models\\Vulnerability': 'vulnerability'
};

// Items that belong to another item: [parent property, parent kind, child property (or null: the entry's own name),
// child label, child kind (for its link)]
const RELATIONS = {
  'App\\Process\\Models\\ProcessPolicy': ['process', 'process', 'policy', 'processes.policies.log.title', 'policy'],
  'App\\Process\\Models\\ProcessInformationObligation': ['process', 'process', 'information_obligation', 'processes.information_obligation.log.title', 'information_obligation'],
  'App\\Process\\Models\\ProcessLegalBasis': ['process', 'process', 'article', 'processes.legal_basis.log.title', 'article'],
  'App\\Process\\Models\\ProcessRiskScenario': ['process', 'process', 'risk_scenario', 'risk_scenarios.log.title', 'risk'],
  'App\\Process\\Models\\ProcessGdpr': ['process', 'process', null, 'processes.legal_basis.log.title'],
  'App\\System\\Models\\SystemPolicy': ['system', 'system', 'policy', 'systems.policies.log.title', 'policy'],
  'App\\System\\Models\\SystemNetwork': ['system', 'system', 'network', 'systems.network.log.title'],
  'App\\System\\Models\\SystemLogManagement': ['system', 'system', 'log_management', 'systems.log_management.log.title'],
  'App\\System\\Models\\SystemDataObject': ['system', 'system', 'data_object', 'systems.data_objects.log.title', 'data_object'],
  'App\\System\\Models\\SystemDataDiscovery': ['system', 'system', 'data_discovery', 'systems.data_discovery.log.title'],
  'App\\System\\Models\\SystemAccessControl': ['system', 'system', 'access_control', 'systems.access_control.log.title'],
  'App\\System\\Models\\SystemThirdParty': ['system', 'system', 'third_party', 'systems.third_parties.log.title', 'contract'],
  'App\\System\\Models\\SystemRiskScenario': ['system', 'system', 'risk_scenario', 'risk_scenarios.log.title', 'risk'],
  'App\\System\\Models\\SystemMeasure': ['system', 'system', 'measure', 'maintenance.measures.log.title', 'measure'],
  'App\\Activity\\Models\\ActivityGdpr': ['activity', 'activity', null, 'activities.legal_basis.log.title'],
  'App\\Activity\\Models\\ActivityPolicy': ['activity', 'activity', 'policy', 'activities.policies.log.title', 'policy'],
  'App\\Activity\\Models\\ActivityDataSubject': ['activity', 'activity', 'data_subject', 'activities.data_subject.log.title', 'data_subject'],
  'App\\Activity\\Models\\ActivityIntegrityControl': ['activity', 'activity', 'integrity_control', 'activities.integrity_control.log.title'],
  'App\\Activity\\Models\\ActivityOrganization': ['activity', 'activity', 'organization', 'activities.organizations.log.title', 'organization'],
  'App\\Activity\\Models\\ActivityRetentionRule': ['activity', 'activity', 'retention_rule', 'activities.retention_rule.log.title', 'retention_rule'],
  'App\\Activity\\Models\\ActivitySystem': ['activity', 'activity', 'system', 'activities.system.log.title', 'system'],
  'App\\Activity\\Models\\ActivityThirdParty': ['activity', 'activity', 'third_party', 'activities.third_party.log.title', 'contract'],
  'App\\Activity\\Models\\ActivityRiskScenario': ['activity', 'activity', 'risk_scenario', 'risk_scenarios.log.title', 'risk'],
  'App\\Activity\\Models\\ActivityMeasure': ['activity', 'activity', 'measure', 'maintenance.measures.log.title', 'measure'],
  'App\\Maintenance\\ControlObjective\\Models\\ControlObjective': ['active_framework', 'framework', null, 'maintenance.control_objective.log.title'],
  'App\\Maintenance\\FrameworkControl\\Models\\FrameworkControl': ['active_framework', 'framework', null, 'maintenance.framework_control.log.title'],
  'App\\GenericControl\\Models\\GenericControlFramework': ['generic_control', 'control', 'framework_control', 'generic_controls.framework_controls.log.title'],
  'App\\GenericControl\\Models\\GenericControlRiskScenario': ['generic_control', 'control', 'risk_scenario', 'generic_controls.risk_scenarios.log.title', 'risk'],
  'App\\GenericControl\\Models\\GenericControlPolicy': ['generic_control', 'control', 'policy', 'generic_controls.policies.log.title', 'policy'],
  'App\\GenericControl\\Models\\GenericControlThreat': ['generic_control', 'control', 'threat', 'generic_controls.threats.log.title', 'threat'],
  'App\\GenericControl\\Models\\GenericControlVulnerability': ['generic_control', 'control', 'vulnerability', 'generic_controls.vulnerabilities.log.title', 'vulnerability'],
  'App\\GenericControl\\Models\\GenericControlMeasure': ['generic_control', 'control', 'measure', 'maintenance.measures.log.title', 'measure'],
  'App\\RiskScenario\\Models\\RiskScenarioThreat': ['risk_scenario', 'risk', 'threat', 'risk_scenarios.threat.log.title', 'threat'],
  'App\\RiskScenario\\Models\\RiskScenarioVulnerability': ['risk_scenario', 'risk', 'vulnerability', 'risk_scenarios.vulnerability.log.title', 'vulnerability'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyDataObject': ['third_party', 'contract', 'data_object', 'maintenance.data_objects.log.title', 'data_object'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyRiskScenario': ['third_party', 'contract', 'risk_scenario', 'risk_scenarios.log.title', 'risk'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyMeasure': ['third_party', 'contract', 'measure', 'maintenance.measures.log.title', 'measure'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyContact': ['third_party', 'contract', null, 'maintenance.third_parties.contacts.log.title'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdPartyDocument': ['third_party', 'contract', null, 'maintenance.third_parties.documents.log.title'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdPartySubdataProcessor': ['third_party', 'contract', 'vendor', 'maintenance.third_parties.sub_data_processors.log.title', 'vendor'],
  'App\\Maintenance\\Policy\\Models\\PolicyDocument': ['policy', 'policy', null, 'maintenance.third_parties.documents.log.title'],
  'App\\Maintenance\\Assessment\\Models\\Question': ['assessment', 'template', null, 'maintenance.questions.log.title', 'question'],
  'App\\Maintenance\\Assessment\\Models\\AssessmentControlObjective': ['assessment', 'template', null, 'template_view.history.objective', 'objective']
};

// Scope items of a project
const SCOPE_KINDS = {
  'App\\Process\\Models\\Process': ['projects.process.label', 'process'],
  'App\\Activity\\Models\\Activity': ['projects.activity.label', 'activity'],
  'App\\System\\Models\\System': ['projects.system.label', 'system'],
  'App\\Maintenance\\ThirdParty\\Models\\ThirdParty': ['projects.third_party.label', 'contract']
};

// Validated items, by the type of the validated item
const VALIDATED = {
  'App\\Process\\Models\\Process': 'process',
  'App\\System\\Models\\System': 'system',
  'App\\Activity\\Models\\Activity': 'activity',
  'App\\Maintenance\\ThirdParty\\Models\\ThirdParty': 'contract',
  'App\\RiskScenario\\Models\\RiskScenario': 'risk',
  'App\\GenericControl\\Models\\GenericControl': 'control',
  'App\\Maintenance\\Policy\\Models\\Policy': 'policy'
};

// Workspace settings: domains, Power BI reports and the API token (US 9391)
const SETTING_LABELS = {
  name: 'profile.client.report_name_label'
};

const ADD = ['added', 'link added', 'attach added'];
const REMOVE = ['removed', 'link removed', 'attach removed'];

// Badge on the avatar
function actionOf(description) {
  if (description === 'created') {
    return 'created';
  }
  if (description === 'deleted') {
    return 'deleted';
  }
  if (ADD.indexOf(description) !== -1) {
    return 'linked';
  }
  if (REMOVE.indexOf(description) !== -1) {
    return 'unlinked';
  }
  if (description === 'imported') {
    return 'uploaded';
  }
  return 'updated';
}

// Labels are lower case nouns ("process"); the chip starts with a capital
function kindLabel(vm, path) {
  const label = vm.$te(path) ? vm.$t(path) : '';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// An item chip: its kind, name and page (only while it still exists)
function chip(vm, kind, object, options = {}) {
  const meta = KINDS[kind] || {};
  const name = options.name !== undefined ? options.name : itemName(object && meta.name ? { name: object[meta.name] || object.name } : object);
  if (!name) {
    return null;
  }
  let to = null;
  const slug = options.slug || (object && object.slug);
  if (!options.gone && meta.route && slug) {
    to = { name: meta.route, params: { slug: slug } };
  } else if (!options.gone && meta.list) {
    to = { name: meta.list };
  }
  return { kind: kind, type: kindLabel(vm, options.label || meta.label), icon: options.icon || meta.icon || 'circle', name: name, to: to, slug: slug };
}

function sentence(path, item, parent, values) {
  return { path: path, item: item, parent: parent, values: values || {} };
}

// The verb of a description, from the actions labels (created, imported, moved …)
function verb(vm, description) {
  const key = 'actions.' + vm.$options.filters.slugify(description);
  return vm.$te(key) ? vm.$t(key) : description;
}

function values(properties) {
  return (properties && properties.attributes) || properties || {};
}

function find(activity, key) {
  const p = activity.properties || {};
  const s = activity.subject || {};
  return p[key] || (p.attributes && p.attributes[key]) || s[key] || (p.model && p.model[key]) || null;
}

function updateChanges(vm, activity, options) {
  const p = activity.properties || {};
  return activity.description === 'updated' && p.attributes ? activityChanges(vm, p.attributes, p.old, options) : [];
}

function describeItem(vm, activity, kind) {
  const p = activity.properties || {};
  const v = values(p);
  const gone = activity.description === 'deleted' || !activity.subject;
  const meta = KINDS[kind];
  const object = Object.assign({}, v, { name: v.name || p.name || (activity.subject && activity.subject.name), title: v.title || p.title || (activity.subject && activity.subject.title) });
  // A client's own changes are logged on the client with kind "client" (US 9391)
  const item = chip(vm, kind, object, { gone: gone, slug: (activity.subject && activity.subject.slug) || v.slug || p.slug, name: meta.name ? (object[meta.name] || object.name) : undefined });
  const entry = { action: actionOf(activity.description), item: item, changes: updateChanges(vm, activity) };
  if (['created', 'updated', 'deleted'].indexOf(activity.description) !== -1) {
    entry.sentence = sentence('activity_log.sentence.' + activity.description, item);
  } else {
    entry.sentence = sentence('activity_log.sentence.generic', item, null, { verb: verb(vm, activity.description) });
  }
  if (activity.description === 'created') {
    entry.values = activityValues(vm, p);
  }
  return entry;
}

function describeRelation(vm, activity, relation) {
  const p = activity.properties || {};
  const [parentKey, parentKind, childKey, childLabel, childKind] = relation;
  const parentObject = find(activity, parentKey);
  const parent = parentObject ? chip(vm, parentKind, parentObject) : null;
  let childObject = childKey ? find(activity, childKey) : values(p);
  // Links and documents on a legal basis: the link or file is the item
  if (activity.description === 'link added' || activity.description === 'link removed') {
    childObject = { name: p.link };
  } else if (activity.description === 'attach added' || activity.description === 'attach removed') {
    childObject = { name: (p.document && (p.document.file_name || p.document.name)) || p.file_name };
  }
  const item = chip(vm, childKind || '', childObject, { label: childLabel, icon: childKind && KINDS[childKind] ? KINDS[childKind].icon : 'link', gone: REMOVE.indexOf(activity.description) !== -1 || activity.description === 'deleted' });
  const entry = { action: actionOf(activity.description), item: item, parent: parent, changes: updateChanges(vm, activity, { hidden: [parentKey, childKey].filter(Boolean) }) };
  // Without its parent (not logged) the entry says what happened to the item only
  let path = 'activity_log.sentence.generic';
  if (parent && (ADD.indexOf(activity.description) !== -1 || activity.description === 'created')) {
    path = 'activity_log.sentence.added';
  } else if (parent && (REMOVE.indexOf(activity.description) !== -1 || activity.description === 'deleted')) {
    path = 'activity_log.sentence.removed';
  } else if (parent && activity.description === 'updated') {
    path = 'activity_log.sentence.relation_updated';
  }
  entry.sentence = sentence(path, item, parent, { verb: verb(vm, activity.description) });
  // A link moved from one system to another (US 9370)
  if (p.moved_from || p.moved_to) {
    entry.notes = [p.moved_from ? vm.$t('system_where_used.log_moved_from', { name: p.moved_from }) : vm.$t('system_where_used.log_moved_to', { name: p.moved_to })];
  }
  if (activity.description === 'moved' && p.from !== undefined) {
    entry.notes = [vm.$t('template_view.history.moved', { from: p.from, to: p.to }) + (p.before ? ' ' + vm.$t('template_view.history.before', { name: p.before }) : '')];
  }
  return entry;
}

function describeValidation(vm, activity) {
  const p = activity.properties || {};
  const relation = p.relation || {};
  const kind = VALIDATED[relation.who_am_i] || '';
  const item = chip(vm, kind, relation, { slug: relation.slug });
  const approved = Number(p.validation) === 1;
  return {
    action: approved ? 'signed' : 'status',
    item: item,
    sentence: sentence('activity_log.sentence.validated', item),
    pill: { tone: approved ? 'green' : 'red', text: approved ? vm.$t('system.approved') : vm.$t('system.rejected') },
    comment: p.comment || null
  };
}

function describeTask(vm, activity) {
  const p = activity.properties || {};
  const v = values(p);
  const name = v.title || p.title || p.full_title;
  const item = chip(vm, 'task', { title: name });
  const entry = { action: actionOf(activity.description), item: item, changes: updateChanges(vm, activity) };
  if (activity.description === 'added') {
    entry.sentence = sentence('activity_log.sentence.created', item);
    if (p.model && p.model.name) {
      entry.context = vm.$t('activity_log.scope') + ': ' + p.model.name;
    }
  } else {
    entry.sentence = sentence('activity_log.sentence.' + (activity.description === 'deleted' ? 'deleted' : 'updated'), item);
  }
  return entry;
}

function describeScopeItem(vm, activity) {
  const p = activity.properties || {};
  const scope = SCOPE_KINDS[p.model_type] || [];
  const item = chip(vm, scope[1] || '', p.model, { label: scope[0] });
  return {
    action: actionOf(activity.description),
    item: item,
    sentence: sentence('activity_log.sentence.' + (activity.description === 'removed' ? 'scope_removed' : 'scope_added'), item)
  };
}

function describeStatementTask(vm, activity) {
  const p = activity.properties || {};
  const text = vm.$t('projects.measures.' + p.field + '_title');
  return {
    action: p.event === 'delivered' ? 'signed' : (p.event === 'returned' ? 'status' : (activity.description === 'link removed' ? 'unlinked' : 'linked')),
    sentence: { text: vm.$t('projects.measures.task_log.' + p.event, { text: text, users: (p.users || []).join(', ') }) },
    notes: p.event === 'returned' && p.message ? [p.message] : []
  };
}

function describeImport(vm, activity) {
  const p = activity.properties || {};
  const item = { kind: 'file', type: vm.$t('template_view.history.audit_plan'), icon: 'file-alt', name: p.file_name || '–', download: p.media_id ? { id: p.media_id, name: p.file_name } : null };
  return {
    action: 'uploaded',
    item: item,
    sentence: sentence('activity_log.sentence.generic', item, null, { verb: verb(vm, 'imported') }),
    notes: [vm.$t('template_view.history.imported_counts', { objectives: p.objectives || 0, controls: p.controls || 0 }) + (p.mode === 'replace' ? ' ' + vm.$t('template_view.history.imported_replaced') : '')]
  };
}

function describeTemplate(vm, activity) {
  const p = activity.properties || {};
  if (activity.description === 'bulk updated') {
    const item = chip(vm, 'template', p);
    const field = vm.$t('task_bulk.fields.' + p.field);
    const value = vm.$te('task_dialog.choices.' + String(p.value).toLowerCase()) ? vm.$t('task_dialog.choices.' + String(p.value).toLowerCase()) : vm.$t('task_bulk.default.' + p.value);
    return {
      action: 'updated',
      item: item,
      sentence: sentence('activity_log.sentence.updated', item),
      // The old values of the tasks aren't logged
      changes: [{ key: p.field, label: field, kind: 'value', from: null, to: value, hadFrom: false }],
      notes: [vm.$tc('template_view.history.bulk_tasks', p.questions, { count: p.questions }) + (p.objective ? ' · ' + p.objective : '')]
    };
  }
  return describeItem(vm, activity, 'template');
}

function describeUser(vm, activity) {
  const p = activity.properties || {};
  const item = chip(vm, 'user', { name: p.name });
  const entry = { action: actionOf(activity.description), item: item };
  if (activity.description === 'merged') {
    entry.sentence = sentence('activity_log.sentence.generic', item, null, { verb: verb(vm, 'merged') });
    if (p.from) {
      entry.notes = [vm.$t('users_page.log.merged_from', { name: p.from.name }) + (!p.kept ? ' ' + vm.$t('users_page.log.merged_removed', { name: p.from.name }) : '')];
    }
    return entry;
  }
  entry.sentence = sentence('activity_log.sentence.' + (['created', 'updated', 'deleted'].indexOf(activity.description) !== -1 ? activity.description : 'updated'), item);
  const labels = {};
  Object.keys(p.attributes || {}).forEach(key => {
    labels[key] = 'users_page.log.fields.' + key;
  });
  entry.changes = activityChanges(vm, p.attributes, p.old, {
    labels: labels,
    formatters: { role: value => vm.$t('maintenance.users.role.' + vm.$options.filters.slugify(value)), dpo: value => Number(value) ? vm.$t('system.yes') : vm.$t('system.no'), locked: value => Number(value) ? vm.$t('system.yes') : vm.$t('system.no') }
  });
  return entry;
}

function describeSetting(vm, activity) {
  const p = activity.properties || {};
  const action = vm.$options.filters.slugify(activity.description);
  return {
    action: actionOf(activity.description),
    sentence: { text: vm.$t('client_page.history.item.' + p.item + '.' + action, { name: p.item_name }) },
    changes: activityChanges(vm, p.attributes, p.old, { labels: SETTING_LABELS })
  };
}

function describeClient(vm, activity) {
  const p = activity.properties || {};
  const isClient = p.kind === 'client';
  const v = values(p);
  const item = chip(vm, 'partition', { name: v.name || p.name }, {
    label: isClient ? 'client_page.history.client' : KINDS.partition.label,
    icon: 'layer-group',
    slug: activity.subject && activity.subject.slug,
    gone: activity.description === 'deleted' || !activity.subject
  });
  if (item && isClient && activity.subject) {
    item.to = { name: 'clientProfile', params: { slug: activity.subject.slug } };
  }
  return {
    action: actionOf(activity.description),
    item: item,
    sentence: sentence('activity_log.sentence.' + (['created', 'updated', 'deleted'].indexOf(activity.description) !== -1 ? activity.description : 'updated'), item),
    changes: activityChanges(vm, p.attributes, p.old, {
      formatters: {
        risk_type: value => vm.$t('profile.client.' + value),
        email_mode: value => vm.$t('client_page.notifications.mode.' + value)
      }
    })
  };
}

/**
 * What an activity entry shows for a log entry.
 *
 * @param vm component, for translations
 * @param {Object} activity log entry from the API
 * @returns {Object} { id, time, causer, action, sentence: { path, item, parent, values } | { text }, context, changes, values, notes, pill, comment, groupKey }
 */
export function describeActivity(vm, activity) {
  const p = activity.properties || {};
  const type = activity.subject_type;
  let entry;
  if (p.engagement_event) {
    entry = describeEngagement(vm, activity);
  } else if (p.item) {
    entry = describeSetting(vm, activity);
  } else if (type === 'App\\Validation\\Models\\Validation') {
    entry = describeValidation(vm, activity);
  } else if (type === 'App\\Project\\Models\\ProjectTask') {
    entry = describeTask(vm, activity);
  } else if (type === 'App\\Project\\Models\\ProjectModel') {
    entry = describeScopeItem(vm, activity);
  } else if (type === 'App\\Project\\Models\\ProjectStatementTask') {
    entry = describeStatementTask(vm, activity);
  } else if (type === 'App\\Maintenance\\Assessment\\Models\\AssessmentImport') {
    entry = describeImport(vm, activity);
  } else if (type === 'App\\Maintenance\\Assessment\\Models\\Assessment') {
    entry = describeTemplate(vm, activity);
  } else if (type === 'App\\Account\\Models\\User') {
    entry = describeUser(vm, activity);
  } else if (type === 'App\\Client\\Models\\Client') {
    entry = describeClient(vm, activity);
  } else if (RELATIONS[type]) {
    entry = describeRelation(vm, activity, RELATIONS[type]);
  } else if (ITEMS[type]) {
    entry = describeItem(vm, activity, ITEMS[type]);
  } else {
    // A type without its own description: who did what to which item
    const item = chip(vm, '', values(p), { label: '' });
    entry = { action: actionOf(activity.description), item: item, sentence: sentence('activity_log.sentence.generic', item, null, { verb: verb(vm, activity.description) }), changes: updateChanges(vm, activity) };
  }
  entry = Object.assign({ changes: [], values: [], notes: [] }, entry);
  entry.id = activity.id;
  entry.time = activity.created_at;
  entry.causer = activity.causer || null;
  // Saves of the same item by the same person can be shown as one entry
  // (the log only sends the subject, so its id comes from there)
  const subjectId = activity.subject ? activity.subject.id : null;
  if (!p.engagement_event && activity.description === 'updated' && entry.changes.length && subjectId) {
    entry.groupKey = type + ':' + subjectId + ':' + (activity.causer ? activity.causer.id : '');
  }
  return entry;
}

export { formatValue };

// The kind of item a page shows (who_am_i), for "no link to the page you're on"
export function kindOf(type) {
  return ITEMS[type] || null;
}
